import React, { useState, useEffect } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { format, differenceInDays } from "date-fns";

function Events() {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [events, setEvents] = useState([]);

  const SHEET_URL =
    "https://docs.google.com/spreadsheets/d/1RCT6-OndGM-VZ7XRNsjNWCW0eL8pXUudgp-kQ11ynr0/gviz/tq?tqx=out:json&sheet=MasjidAboTherEventSheet";

  function parseGoogleDate(str) {
    if (!str) return null;
    if (str instanceof Date) return str;

    if (typeof str === "string" && str.startsWith("Date(")) {
      try {
        const parts = str
          .replace("Date(", "")
          .replace(")", "")
          .split(",")
          .map((x) => parseInt(x.trim(), 10));

        const [year, month, day, hour = 0, minute = 0, second = 0] = parts;
        return new Date(year, month, day, hour, minute, second);
      } catch {
        return null;
      }
    }

    const d = new Date(str);
    return isNaN(d) ? null : d;
  }

  useEffect(() => {
    async function fetchEvents() {
      try {
        const res = await fetch(SHEET_URL);
        const text = await res.text();
        console.log("Sheet response:", text);

        const json = JSON.parse(text.substr(47).slice(0, -2));

        const rows = json.table.rows.map((r) => {
          const date = parseGoogleDate(r.c[0]?.v);
          const time = parseGoogleDate(r.c[2]?.v);

          const timeFormatted = time
            ? time.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
            : "";

          return {
            date,
            title: r.c[1]?.v || "",
            time: timeFormatted,
            description: r.c[3]?.v || "",
          };
        });

        setEvents(rows.filter((e) => e.date instanceof Date && !isNaN(e.date)));
      } catch (err) {
        console.error("Error loading Google Sheet data:", err);
      }
    }

    fetchEvents();
    const interval = setInterval(fetchEvents, 60000 * 5);
    return () => clearInterval(interval);
  }, []);

  const eventsForSelectedDate = events.filter(
    (event) =>
      event.date &&
      !isNaN(event.date) &&
      format(event.date, "yyyy-MM-dd") === format(selectedDate, "yyyy-MM-dd")
  );

  const now = new Date();

  return (
    <div style={styles.page}>
      <h1 style={styles.heading}>Mosque Events Calendar</h1>
      <p style={styles.subtitle}>Tap a date to view events</p>

      {/* Calendar */}
      <div style={styles.calendarWrapper}>
        <Calendar
          onChange={setSelectedDate}
          value={selectedDate}
         tileContent={({ date }) => {
        const eventsOnDate = events.filter(
          (e) =>
            format(e.date, "yyyy-MM-dd") === format(date, "yyyy-MM-dd")
        );

        if (eventsOnDate.length === 0) return null;

        const now = new Date();
        let hasFuture = false;
        let hasPast7 = false;

        eventsOnDate.forEach((event) => {
          if (event.date > now) {
            hasFuture = true;
          } else {
            const diffMs = now - event.date;
            const diffDays = diffMs / (1000 * 60 * 60 * 24);

            if (diffDays <= 7) {
              hasPast7 = true;
            }
          }
        });

        if (hasFuture) {
          return <div style={{ ...styles.eventDot, backgroundColor: "#067446" }} />;
        }

        if (hasPast7) {
          return <div style={{ ...styles.eventDot, backgroundColor: "#999" }} />;
        }

        return null;
      }}

          className="modern-calendar"
        />
      </div>

    {/* Events List */}
    <div style={styles.eventList}>
      {eventsForSelectedDate.length > 0 ? (
        eventsForSelectedDate.map((event, i) => (
          <div key={i} style={styles.eventCard}>
            <div style={styles.eventHeader}>
              <span style={styles.eventDate}>
                {format(event.date, "EEEE, MMMM d, yyyy")}
              </span>
            </div>

            <h3 style={styles.eventTitle}>{event.title}</h3>

            <div style={styles.eventInfoRow}>
              <span style={styles.eventTime}>
                <strong>🕒</strong> {event.time || "N/A"}
              </span>
              <span style={styles.separator}> | </span>
              <span style={styles.eventDesc}>{event.description}</span>
            </div>
          </div>
        ))
      ) : (
        <p style={styles.noEvents}>No events scheduled for this day.</p>
      )}
    </div>

      <style>
        {`
          .modern-calendar {
            border: none !important;
            box-shadow: 0 4px 15px rgba(0,0,0,0.08);
            border-radius: 16px;
            padding: 10px;
            background: #ffffff;
            font-family: 'Georgia', serif;
          }

          .react-calendar__tile--active {
            background: #067446 !important;
            color: white !important;
            border-radius: 8px;
          }

          .react-calendar__tile--now {
            background: #d19f14 !important;
            color: white !important;
            border-radius: 8px;
          }

          .react-calendar__tile:hover {
            background: #e8f5f1 !important;
            border-radius: 8px;
          }
        `}
      </style>
    </div>
  );
}

const styles = {
  page: {
    backgroundColor: "#fff",
    fontFamily: "'Georgia', serif",
    padding: "0px 25px",
    textAlign: "center",
    color: "#333",
  },
  heading: {
    color: "#067446",
    fontSize: "2.2rem",
    marginBottom: "10px",
    fontWeight: "400",
  },
  subtitle: {
    color: "#d19f14",
    fontSize: "1rem",
    marginBottom: "25px",
    fontStyle: "italic",
  },
  calendarWrapper: {
    display: "flex",
    justifyContent: "center",
    marginBottom: "40px",
  },
  eventDot: {
    height: "8px",
    width: "8px",
    borderRadius: "50%",
    margin: "0 auto",
    marginTop: "3px",
  },
  eventList: {
    maxWidth: "700px",
    margin: "0 auto",
    backgroundColor: "#f9f9f9",
    borderRadius: "16px",
    boxShadow: "0 4px 15px rgba(0,0,0,0.08)",
    padding: "30px",
  },
  eventCard: {
    backgroundColor: "#ffffff",
    padding: "20px 25px",
    borderLeft: "6px solid #d19f14",
    borderRadius: "12px",
    marginBottom: "20px",
    textAlign: "left",
    boxShadow: "0 3px 10px rgba(0,0,0,0.05)",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  eventHeader: {
    marginBottom: "8px",
  },
  eventDate: {
    color: "#d19f14",
    fontWeight: "bold",
    fontSize: "1rem",
  },
  eventContent: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  eventTitle: {
    color: "#067446",
    fontSize: "1.3rem",
    margin: 0,
  },
  eventDetails: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  eventTime: {
    color: "#333",
    fontWeight: "500",
    margin: 0,
  },
  eventDesc: {
    color: "#555",
    margin: 0,
  },
  noEvents: {
    color: "#888",
    fontStyle: "italic",
    marginTop: "10px",
  },
};

export default Events;
